import { IncidentStatus } from "../../types/fieldInstance.interface";
import { incidentRelatedLists } from "../helpers/relatedListHelpers";
import {
  populateInlineRLs,
  transformArrayValues,
} from "./transformArrayValues";
import { toast } from "react-toastify";

function getUpdatedRecord(fetchedRecords: any[], savedRecords: any[]) {
  const _fetched = fetchedRecords ?? [];
  const _saved = savedRecords ?? [];
  // Build a lookup map for O(n) merge instead of O(n²) .find() per record.
  const savedById = new Map(_saved.filter((r) => r.Id).map((r) => [r.Id, r]));
  const merged = _fetched.map((fetchedRecord) => {
    if (!fetchedRecord.Id) return fetchedRecord;
    const savedRecord = savedById.get(fetchedRecord.Id);
    return savedRecord ? { ...fetchedRecord, ...savedRecord } : fetchedRecord;
  });
  const unsavedNew = _saved.filter((rec) => !rec.Id);
  return [...merged, ...unsavedNew];
}

function updateSelectedIncident(
  transformedSelectedIncident: any,
  incidentFormValues: any,
  deleteRelatedRecords: any,
  shouldUpdateForm: boolean
) {
  // Map through the array values in the incident related list, if the record has the attribute property, check that its Id is not in deletedRecords
  // If it is not in deletedRecords, add to the array, otherwise, remove from the array
  const relatedRecordKeys = Object.values(incidentRelatedLists);
  return Object.keys(incidentFormValues).reduce((acc: any, key: any) => {
    if (relatedRecordKeys.includes(key)) {
      const deletedRecords = deleteRelatedRecords.filter(
        (record: any) => record?.Sobject && record.Sobject === key
      );
      const updatedBERecords = transformedSelectedIncident[key].filter(
        (record: any) => !deletedRecords.includes(record.Id)
      );
      const updatedRecords = shouldUpdateForm
        ? updatedBERecords
        : getUpdatedRecord(updatedBERecords, incidentFormValues[key]);
      acc[key] = updatedRecords;
    } else {
      acc[key] = shouldUpdateForm
        ? transformedSelectedIncident[key]
        : incidentFormValues[key];
    }
    return { ...acc };
  }, {});
}

/**
 * Normalizes "Not Listed" togglebox values based on their parent lookup fields.
 * If a lookup field has a value, the corresponding "Not Listed" checkbox should be false.
 * This handles cases where the API might return inconsistent data.
 * 
 * Dynamically identifies "Not Listed" fields by their naming pattern (ends with _Not_Listed__c)
 * and derives the parent lookup field by removing the suffix.
 */
const normalizeNotListedFields = (data: any) => {
  const normalized = { ...data };
  
  // Find all "Not Listed" fields in the data
  Object.keys(normalized).forEach((key) => {
    // Match fields ending with _Not_Listed__c or _Not_listed__c (case variations exist)
    const notListedMatch = key.match(/^(.+)_Not_[Ll]isted__c$/);
    
    if (notListedMatch) {
      // Derive potential parent field names
      const baseFieldName = notListedMatch[1];
      const possibleParentFields = [
        `${baseFieldName}__c`,           // Standard pattern: Field__c / Field_Not_Listed__c
        `${baseFieldName}`,              // Edge case: Field_Name / Field_Name_Not_Listed__c
      ];
      
      // Check if any parent field has a value
      const parentHasValue = possibleParentFields.some(
        (parentField) => normalized[parentField] && normalized[parentField] !== ""
      );
      
      // If parent has value, ensure "Not Listed" is false
      if (parentHasValue) {
        normalized[key] = false;
      }
    }
  });

  return normalized;
};

const getTransformedData = (selectedIncident: any, initialValues: any) => {
    const transformedSelectedIncident = {
      ...initialValues,
      ...selectedIncident,
      ...transformArrayValues(initialValues, selectedIncident),
    };
    // Normalize "Not Listed" fields to ensure data consistency
    const normalizedData = normalizeNotListedFields(transformedSelectedIncident);
    return {
      ...normalizedData,
    ...populateInlineRLs(normalizedData, initialValues),
    };
};
export const getDefaultValues = (
  incidentStatus: any,
  selectedIncident: any,
  initialValues: any,
  incidentFormValues: any,
  formBasicDetails: any,
  deleteRelatedRecords: any,
  shouldUpdateForm: boolean
) => {
  try {
    const transformedSelectedIncident = selectedIncident
      ? getTransformedData(selectedIncident, initialValues)
      : null;

  // When shouldUpdateForm is true, API data should take precedence over stored form values
  // This ensures that when loading a new incident, the API values are used.
  // EXCEPTION: For drafts where the user has already started editing (incidentFormValues
  // exists in the store), user edits must always take precedence over API data.
  // Without this, tab navigation or React Query refetches would overwrite user edits
  // with the draft's stale/empty API values.
  const isDraftWithUserEdits =
    incidentStatus === IncidentStatus.Draft &&
    incidentFormValues &&
    Object.keys(incidentFormValues).length > 0;

  const shouldPrioritizeAPIData = shouldUpdateForm && !isDraftWithUserEdits;

  let updatedSelectedIncident = shouldPrioritizeAPIData
    ? {
        ...(incidentFormValues ?? {}),
        ...transformedSelectedIncident,
      }
    : {
        ...transformedSelectedIncident,
        ...(incidentFormValues ?? {}),
      };

  const isSubmitted =
    !!incidentStatus && incidentStatus !== IncidentStatus.Draft;
  if (incidentFormValues && isSubmitted && selectedIncident) {
    updatedSelectedIncident = updateSelectedIncident(
      transformedSelectedIncident,
      incidentFormValues,
      deleteRelatedRecords,
      shouldUpdateForm
    );
  }

  let defaultFormValues = incidentStatus
    ? { ...transformedSelectedIncident, ...updatedSelectedIncident }
    : incidentFormValues;

  return defaultFormValues && Object.keys(defaultFormValues).length
    ? defaultFormValues
    : {
        ...initialValues,
        ...formBasicDetails,
      };
  } catch (error) {
    
    
    // Extract field name from error message if possible
    let errorMessage = "Some form fields could not be loaded. You can still fill them in manually.";
    const errorStr = String(error);
    
    // Extract field name from error message like "Cannot read properties of undefined (reading 'Care_Provided_Prior_To_Incident__c')"
    const fieldMatch = errorStr.match(/reading '([^']+)'/);
    if (fieldMatch && fieldMatch[1]) {
      const fieldName = fieldMatch[1].replace(/_/g, " ").replace(/__c$/, "");
      errorMessage = `The "${fieldName}" field could not be loaded. Please update this field.`;
    }
    
    // Show a simple, user-friendly toast message
    toast.error(errorMessage, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
      toastId: "form-data-error"
    });
    
    // Return a safe fallback value
    return {
      ...initialValues,
      ...formBasicDetails,
    };
  }
};